import type { VideoModel } from '..';
import {
    BaseVideoProvider,
    type VideoProviderAdapter,
    TaskStatus,
    type CreateTaskConfig,
    type CreateTaskResult,
    type PollTaskConfig,
    type PollTaskResult,
} from '../base';
import { buildInputPayloadFromConfig, getKieGeneralModelConfig } from './utils';
import type { StatusMapping, KieGeneralCreateResponse, KieGeneralTaskResponse } from './types';
import { getValueByPath, isStatusMatch } from '../tools';

// 创建提供商实例
const provider = new BaseVideoProvider({
    baseUrlEnv: 'KIE_AI_BASE_URL',
    apiKeyEnv: 'KIE_AI_API_KEY',
    debugEnv: 'KIE_AI_DEBUG',
    providerName: 'KIE_GENERAL',
});

// 实现适配器
const adapter: VideoProviderAdapter = {
    // 构建**任务创建**请求
    async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
        // 获取模型配置
        const config = getKieGeneralModelConfig(modelId);
        const apiPath = config.apiPath;
        // 构建输入参数（可能是异步构建）
        const payload = await buildInputPayloadFromConfig(modelId, params);
        // 返回请求配置
        return {
            url: `${context.baseUrl}${apiPath}`,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
            body: payload,
        };
    },

    // 解析**任务创建**响应
    parseCreateTaskResponse(modelId, response, data: KieGeneralCreateResponse): CreateTaskResult {
        // 获取服务商配置
        const config = getKieGeneralModelConfig(modelId);
        const taskIdPath = config.statusMapping.taskIdPath;
        const taskId = getValueByPath(data, taskIdPath);
        if (!taskId) {
            throw new Error(`Failed to get task id from response`);
        }
        // 返回任务 ID
        return {
            taskId: taskId,
            raw: data,
        };
    },

    // 构建**轮询任务**请求
    buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
        // 获取模型配置
        const config = getKieGeneralModelConfig(modelId);
        const pollApiPath = config.pollApiPath;
        // 返回请求配置
        return {
            url: (taskId) => `${context.baseUrl}${pollApiPath}?taskId=${taskId}`,
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
        };
    },

    // 解析**轮询任务**响应
    parsePollTaskResponse(modelId, response, data: KieGeneralTaskResponse): PollTaskResult {
        // 读取服务商配置 (状态映射)
        const config = getKieGeneralModelConfig(modelId);
        const statusMapping = config.statusMapping;
        // 获取任务状态
        const state = getValueByPath(data, statusMapping.statePath);
        // 如果状态值未找到则视为**处理中**
        if (state === undefined) {
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }
        // 1. 判断**成功**状态
        if (isStatusMatch(state, statusMapping.success)) {
            // 获取结果字符串
            const resultJsonStr = getValueByPath(data, statusMapping.videoUrlPath);
            // 获取视频 URL
            const videoUrl = Array.isArray(resultJsonStr) ? resultJsonStr[0] : resultJsonStr;
            // 视频 URL 不存在则视为**失败**状态
            if (!videoUrl || typeof videoUrl !== 'string') {
                return {
                    status: TaskStatus.FAILED,
                    error: 'Missing video URL in result',
                    raw: data,
                };
            }
            // 返回**成功**状态
            return {
                status: TaskStatus.SUCCESS,
                videoUrl,
                raw: data,
            };
        }
        // 2. 判断**失败**状态
        const error = getValueByPath(data, statusMapping.errorPath);
        // 如果 failed 为空数组，则通过 errorPath 字段是否存在来判断失败
        if (statusMapping.failed && statusMapping.failed.length === 0) {
            if (error !== undefined && error !== null && error !== '') {
                return {
                    status: TaskStatus.FAILED,
                    error: String(error) || 'unknown error',
                    raw: data,
                };
            }
        } else if (isStatusMatch(state, statusMapping.failed)) {
            return {
                status: TaskStatus.FAILED,
                error: String(error) || 'unknown error',
                raw: data,
            };
        }

        // 3. 判断**处理中**状态
        if (isStatusMatch(state, statusMapping.processing)) {
            // 返回**处理中**状态
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }

        // 4. 判断**未知**状态 (不在配置范围内)
        // 返回**处理中**状态
        return {
            status: TaskStatus.PROCESSING,
            raw: data,
        };
    },
};

// 导出工厂函数
export const kie_general = (modelId: string): VideoModel => {
    const config = getKieGeneralModelConfig(modelId);
    return provider.createVideoModel(modelId, adapter);
};